import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CCol, CRow, CForm, CFormInput, CFormLabel, CFormSelect, CFormTextarea, CButton, CAlert,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CCard, CCardHeader, CCardBody, CInputGroup, CInputGroupText, CSpinner, CFormCheck,
  
  CBadge,
  CTabs, CTabList, CTab, CTabContent, CTabPanel,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilUser, cilPhone, cilLocationPin, cilMap, cilAddressBook, cilUserFollow, cilPencil,
} from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { fmtMoneda } from '../utils/moneda'
import { numeroWhatsApp, prefijoPais } from '../utils/whatsapp'
import WwGrid, { WwColumn, WwRowAction } from '../components/ww/WwGrid'
import WwFilterBar from '../components/ww/WwFilterBar'
import { usePendiente } from '../components/ww/pendiente'
import FirmaCanvas from '../components/ww/FirmaCanvas'

// Grilla de clientes — réplica de clienteww.cs:167 (filtro por ClienteCedula)
// y alta/BC de cliente (cliente_bc.cs:191 IsIns). La cédula se muestra SIN
// separadores (type 'id', regla del proyecto).
const columnas: WwColumn[] = [
  { key: 'clienteCedula', title: 'Cedula', type: 'id', link: true },
  { key: 'clienteNombre', title: 'Nombre' },
  {
    // [RGM] 2026-09-23 (regla Rafael): "Se observa" = ClienteObservacion
    // (singular — distinto de ClienteObservaciones del form). Si la
    // observación NO existe, se GENERA el texto según la CANTIDAD de
    // préstamos: 1 = "Nuevo"; N = "Renovó N−1" (ej. 2 → "Renovó 1").
    // (Esta columna absorbe la antigua columna "#".)
    key: 'clienteObservacion', title: 'Se observa', minWidth: 130,
    render: (row) => {
      const obs = String(row.clienteObservacion ?? '').trim()
      if (obs) return obs
      const n = Number(row.clienteCantidadPrestamos ?? 0)
      if (n <= 0) return '—'
      if (n === 1) return 'Nuevo'
      return `Renovó ${n - 1}`
    },
    // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
    // mismo texto generado que el render (observación / Nuevo / Renovó N / —).
    exportValue: (row) => {
      const obs = String(row.clienteObservacion ?? '').trim()
      if (obs) return obs
      const n = Number(row.clienteCantidadPrestamos ?? 0)
      if (n <= 0) return '—'
      if (n === 1) return 'Nuevo'
      return `Renovó ${n - 1}`
    },
  },
  { key: 'clienteNegocio', title: 'Negocio' },
  { key: 'clienteDireccion', title: 'Direccion' },
  { key: 'clienteBarrioDireccion', title: 'Barrio' },
  { key: 'clienteCelular', title: 'Celular' },
  { key: 'clienteCodeudorNombre', title: 'Codeudor' },
  { key: 'clienteCodeudorCelular', title: 'Codeudor Celular' },
  { key: 'zonaNombre', title: 'Ruta Nombre' },
  { key: 'clienteCreadoPor', title: 'Creado Por' },
  {
    key: 'clienteEstado', title: 'Estado',
    // Badge de estado del cliente (clienteww.cs:4014 — combo ClienteEstado GX:
    // Activo, Inactivo, Pausado, Eliminado)
    render: (row) => {
      const e = String(row.clienteEstado ?? '').trim()
      const color = e === 'Activo' ? 'success'
        : e === 'Nuevo' ? 'info'
        : e === 'Inactivo' ? 'warning'
        : e === 'Pausado' ? 'secondary'
        : e === 'Eliminado' ? 'danger'
        : 'secondary'
      return <CBadge color={color}>{e || '—'}</CBadge>
    },
    // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
    // exportValue espeja el render (trim + fallback '—').
    exportValue: (row) => {
      const e = String(row.clienteEstado ?? '').trim()
      return e || '—'
    },
  },
]

// Columnas de los tabs del cliente (consolidado — arptextractoconsolidado.cs)
const columnasConsolidadoPrestamos: WwColumn[] = [
  { key: 'prestamoNro', title: 'Nro', type: 'number', link: true },
  { key: 'prestamoTipo', title: 'Tipo' },
  { key: 'prestamoFecha', title: 'Fecha', type: 'date' },
  { key: 'prestamoMPago', title: 'MPago', type: 'number' },
  { key: 'prestamoValor', title: 'Valor', type: 'currency' },
  { key: 'prestamoCuota', title: 'Cuota', type: 'currency' },
  { key: 'prestamoCuotas', title: 'Cuotas', type: 'number' },
  { key: 'prestamoAbonos', title: 'Abonos', type: 'currency' },
  { key: 'prestamoSaldo', title: 'Saldo', type: 'currency' },
  { key: 'saldoReal', title: 'Saldo Real', type: 'currency' },
  {
    key: 'prestamoEstado', title: 'Estado', sortable: true,
    // Estado CALCULADO (EstadoPrestamo.Calcular — backend; estadoruta.cs):
    // Activo verde, Nuevo celeste, No Pagó naranja, Vencido rojo, Pagado azul.
    render: (row) => {
      const e = String(row.prestamoEstado ?? '').trim()
      const color = e === 'Activo' ? 'success'
        : e === 'Nuevo' ? 'info'
        : e === 'No Pagó' ? 'warning'
        : e === 'Vencido' ? 'danger'
        : e === 'Pagado' ? 'primary'
        : 'secondary'
      return <CBadge color={color}>{e || '—'}</CBadge>
    },
    // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
    // exportValue espeja el render (trim + fallback '—').
    exportValue: (row) => {
      const e = String(row.prestamoEstado ?? '').trim()
      return e || '—'
    },
  },
]

// Préstamos del cliente CONSOLIDADO agrupados por ESTADO (réplica
// estadoruta.cs — el backend ya manda prestamoEstado calculado). Un tab por
// estado: Todos / Activos / Nuevos / No Pagó / Vencidos / Pagados (solo los
// que tienen al menos 1 préstamo), con su conteo en el label.
function ConsolidadoPorEstado({ prestamos, mostrarCedula = false }: { prestamos: any[]; mostrarCedula?: boolean }) {
  const [tab, setTab] = useState('todos')
  const grupos: { key: string; label: string; test: (p: any) => boolean }[] = [
    { key: 'todos', label: 'Todos', test: () => true },
    { key: 'activos', label: 'Activos', test: (p) => p.prestamoEstado === 'Activo' },
    { key: 'nuevos', label: 'Nuevos', test: (p) => p.prestamoEstado === 'Nuevo' },
    { key: 'nopago', label: 'No Pagó', test: (p) => p.prestamoEstado === 'No Pagó' },
    { key: 'vencidos', label: 'Vencidos', test: (p) => p.prestamoEstado === 'Vencido' },
    { key: 'pagados', label: 'Pagados', test: (p) => p.prestamoEstado === 'Pagado' },
  ]
  // [RGM] 2026-10-01 (extensión "clonado de cliente"): en modo "Incluir
  // clones" cada fila viene de una cédula/ruta distinta — columna adicional
  // SOLO en ese modo (la vista normal queda intacta).
  const columnas = mostrarCedula
    ? [
        ...columnasConsolidadoPrestamos,
        {
          key: 'cedulaOrigen', title: 'Cédula', minWidth: 110,
          render: (row: any) => (
            <>
              {row.cedulaOrigen ?? '—'}
              <br />
              <small className="text-body-secondary">{row.rutaOrigen || '—'}</small>
            </>
          ),
        },
      ]
    : columnasConsolidadoPrestamos
  if (prestamos.length === 0) {
    return <div className="text-body-secondary py-3 text-center">Sin préstamos</div>
  }
  const visibles = grupos.filter((g) => prestamos.some(g.test))
  const activo = visibles.some((g) => g.key === tab) ? tab : 'todos'
  const grupoActivo = grupos.find((g) => g.key === activo)!
  const filas = prestamos.filter(grupoActivo.test)
  return (
    <CTabs activeItemKey={activo} onChange={(k) => setTab(String(k))} className="mb-2">
      <CTabList variant="pills">
        {visibles.map((g) => (
          <CTab key={g.key} itemKey={g.key}>
            {g.label} ({prestamos.filter(g.test).length})
          </CTab>
        ))}
      </CTabList>
      <CTabContent>
        <CTabPanel itemKey={activo} className="pt-2">
          <WwGrid
            title={`Préstamos — ${grupoActivo.label}`}
            columns={columnas}
            rows={filas}
            onTotalChange={() => undefined}
          />
        </CTabPanel>
      </CTabContent>
    </CTabs>
  )
}

// Formato de moneda: símbolo configurado por empresa + máscara GX
// "$ Z,ZZZ,ZZZ,ZZ9.99" (arptcortecajadiario.cs:184) — fuente única
// utils/moneda.ts (fmtMoneda). [RGM] 2026-10-02 (F)

const formVacio = {
  clienteCedula: '', nombre: '', negocio: '', estado: 'Nuevo',
  celular: '', telefono: '', email: '', direccion: '', barrioDireccion: '',
  residencia: '', barrioResidencia: '', geolocalizacion: '', zonaCodigo: '',
  posicionRuta: '', referencia1: '', referencia2: '',
  codeudorCedula: '', codeudorNombre: '', codeudorDireccion: '',
  codeudorTelefono: '', codeudorCelular: '',
  observaciones: '', fotoBase64: '', fotoContentType: '',
  fotoUrl: '', firmaClienteUrl: '', firmaCodeudorUrl: '',
  firmaClienteBase64: '', firmaCodeudorBase64: '',
  // [RGM] 2026-10-01 (extensión "clonado de cliente"): cédula del original
  // cuando el alta es un clon ('' = alta normal).
  clonadoDe: '',
}

const estados = ['Nuevo', 'Activo', 'Inactivo', 'Pausado', 'Eliminado']

const ClientesPage: React.FC = () => {
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  // ── Filtros rápidos: estado y ruta ──
  const [filtroEstado, setFiltroEstado] = useState('')
  const [filtroRuta, setFiltroRuta] = useState('')
  // ── Selección de cliente (colapso + tabs — patrón Empresas superadmin) ──
  const [seleccionado, setSeleccionado] = useState<any | null>(null)
  const [consolidado, setConsolidado] = useState<any | null>(null)
  const [cargandoConsolidado, setCargandoConsolidado] = useState(false)
  // [RGM] 2026-10-01 (extensión "clonado de cliente" — aprobada por Rafael):
  // "Incluir clones" MERGEA el consolidado de las cédulas vinculadas (opt-in,
  // default OFF: la vista actual no cambia) y carga esos consolidados con el
  // mismo endpoint por cédula.
  const [incluirClones, setIncluirClones] = useState(false)
  const [clonesConsolidados, setClonesConsolidados] = useState<any[]>([])
  const [cargandoClones, setCargandoClones] = useState(false)
  // Tab Facturas SOLO si la empresa tiene el módulo de facturación activo
  // (EmpresaModuloFacturacion — CuentaController.cs:78, MenuController modfactura)
  const [modFacturacion, setModFacturacion] = useState(false)
  // Módulo de FOTOS = EmpresaFactura (caption GX "Fotos Adicional Mes" — el
  // nombre engaña, la descripción manda; SuperAdminEmpresasPage lo gestiona).
  const [modFotos, setModFotos] = useState(false)

  useEffect(() => {
    api.get<any>('/cuenta?empresa=' + empresa)
      .then((cuenta) => {
        // [RGM] 2026-10-07 (pedido Rafael: "hay opciones que se activan
        // validando modo facturación... en modo facturación debe activarse
        // móduloFacturación + soloFacturación"): la capacidad de facturación
        // también viene dada por el motor SOLO FACTURACIÓN (el módulo puede
        // estar aún pendiente de pago y la facturación igual aplica).
        setModFacturacion(Number(cuenta.moduloFacturacion ?? 0) === 1
          || String(cuenta.modo ?? '') === 'FACTURACION')
        setModFotos(cuenta.fotosAdicionalMes === true)
      })
      .catch(() => undefined)
  }, [empresa])
  const [rutas, setRutas] = useState<any[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const { aviso, setAviso } = usePendiente()
  // Fila en edición (clienteww.cs:2902-2904 'DO UPDATE' → webobject cliente Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  // [RGM] 2026-10-01 (extensión "clonado de cliente"): fila ORIGEN cuando el
  // modal de alta viene de "Clonar" (guarda oculto clonadoDe = su cédula y
  // deja la cédula nueva vacía para teclearla).
  const [clonando, setClonando] = useState<any | null>(null)
  // [RGM] 2026-10-01 (extensión "clonado-multiruta", aprobada por Rafael):
  // rutas YA ocupadas por la familia al clonar — el combo Ruta las muestra
  // disabled ("ya está en esta ruta") para que la misma persona no quede dos
  // veces en una ruta. Solo se llena en abrirClonar (consolidado del origen);
  // se limpia al cerrar el modal, en abrirNuevo y en abrirEditar.
  const [rutasOcupadas, setRutasOcupadas] = useState<Set<number>>(new Set())
  const [errorModal, setErrorModal] = useState('')
  const formRef = useRef<HTMLFormElement>(null)
  const [form, setForm] = useState(formVacio)

  // Filas filtradas por estado/ruta (client-side, patrón Préstamos)
  const visibleRows = useMemo(() => rows.filter((row) => {
    if (filtroEstado && String(row.clienteEstado ?? '').trim() !== filtroEstado) return false
    if (filtroRuta && String(row.zonaNombre ?? '').trim() !== filtroRuta) return false
    return true
  }), [rows, filtroEstado, filtroRuta])

  const estadosCliente = useMemo(() => [...new Set(rows.map((r) => String(r.clienteEstado ?? '').trim()).filter(Boolean))], [rows])
  const rutasCliente = useMemo(() => [...new Set(rows.map((r) => String(r.zonaNombre ?? '').trim()).filter(Boolean))], [rows])

  const chips = [
    filtroEstado ? { clave: 'estado', label: `Estado: ${filtroEstado}` } : null,
    filtroRuta ? { clave: 'ruta', label: `Ruta: ${filtroRuta}` } : null,
  ].filter(Boolean) as { clave: string; label: string }[]

  const limpiarFiltros = () => { setFiltroEstado(''); setFiltroRuta('') }
  const quitarFiltro = (clave: string) => {
    if (clave === 'estado') setFiltroEstado('')
    if (clave === 'ruta') setFiltroRuta('')
  }

  // Consolidado del cliente seleccionado (réplica arptextractoconsolidado.cs)
  useEffect(() => {
    if (!seleccionado) { setConsolidado(null); return }
    // Al cambiar de cliente, el merge de clones vuelve a OFF (default).
    setIncluirClones(false)
    setClonesConsolidados([])
    setCargandoConsolidado(true)
    api.get<any>(`/clientes/${seleccionado.clienteCedula}/consolidado?empresa=${empresa}`)
      .then(setConsolidado)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargandoConsolidado(false))
  }, [seleccionado, empresa])

  // [RGM] 2026-10-01: al activar "Incluir clones" se carga el consolidado de
  // cada cédula VINCULADA (el original si este es clon, y/o sus clones) con
  // el endpoint existente por cédula. El backend no cambia.
  useEffect(() => {
    if (!incluirClones || !seleccionado || !consolidado) { setClonesConsolidados([]); return }
    const vinculadas: { cedula: any; rutaNombre: string }[] = []
    if (consolidado.clonadoDe) {
      vinculadas.push({ cedula: consolidado.clonadoDe.cedula, rutaNombre: consolidado.clonadoDe.rutaNombre ?? '' })
    }
    for (const cl of (consolidado.clones ?? [])) {
      vinculadas.push({ cedula: cl.cedula, rutaNombre: cl.rutaNombre ?? '' })
    }
    const propias = vinculadas.filter((v) => String(v.cedula ?? '') !== String(seleccionado.clienteCedula ?? ''))
    if (propias.length === 0) { setClonesConsolidados([]); return }
    let cancelado = false
    setCargandoClones(true)
    Promise.all(propias.map(async (v) => {
      try {
        const c = await api.get<any>(`/clientes/${v.cedula}/consolidado?empresa=${empresa}`)
        return { ...c, zonaNombre: v.rutaNombre }
      } catch {
        return null
      }
    })).then((res) => {
      if (!cancelado) setClonesConsolidados(res.filter(Boolean))
    }).finally(() => {
      if (!cancelado) setCargandoClones(false)
    })
    return () => { cancelado = true }
  }, [incluirClones, consolidado, seleccionado, empresa])

  // [RGM] 2026-10-01: préstamos del consolidado. En modo "Incluir clones" se
  // MERGEAN los de las cédulas vinculadas y cada fila queda marcada con su
  // cédula/ruta de origen (columna adicional solo en ese modo).
  const prestamosConsolidado = useMemo(() => {
    const base = (consolidado?.prestamos ?? []).map((p: any) => ({
      ...p,
      cedulaOrigen: seleccionado?.clienteCedula,
      rutaOrigen: seleccionado?.zonaNombre ?? '',
    }))
    if (!incluirClones) return base
    return [
      ...base,
      ...clonesConsolidados.flatMap((c: any) =>
        (c.prestamos ?? []).map((p: any) => ({
          ...p,
          cedulaOrigen: c.cliente?.clienteCedula,
          rutaOrigen: c.zonaNombre ?? '',
        })),
      ),
    ]
  }, [consolidado, incluirClones, clonesConsolidados, seleccionado])

  // [RGM] 2026-10-01: selecciona en la página la cédula vinculada (mismo
  // mecanismo del grid: setSeleccionado con la fila de rows; si no está en la
  // lista actual, navega al detalle).
  const verClienteVinculado = (cedula: any) => {
    const fila = rows.find((r) => String(r.clienteCedula ?? '') === String(cedula ?? ''))
    if (fila) { setSeleccionado(fila); return }
    navigate(`/clientes/${cedula}`)
  }

  // PDF del consolidado (RptExtractoConsolidado — réplica
  // arptextractoconsolidado.cs; GET /clientes/{cedula}/consolidado.pdf).
  // [RGM] 2026-10-03 (pedido Rafael): abre en pestaña nueva vía api.abrirPdf
  // (antes descargaba con blob + anchor).
  const descargarConsolidadoPdf = async (fila: any) => {
    try {
      await api.abrirPdf(`/clientes/${fila.clienteCedula}/consolidado.pdf?empresa=${empresa}`)
    } catch (e: any) {
      setError(e.message ?? 'No se pudo abrir el consolidado')
    }
  }

  const cargar = () => {
    setCargando(true)
    api.get<any[]>(`/clientes?empresa=${empresa}`)
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [empresa])

  useEffect(() => {
    api.get<any[]>(`/rutas?empresa=${empresa}`)
      .then(setRutas)
      .catch(() => setRutas([]))
  }, [empresa])

  const setCampo = (campo: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [campo]: e.target.value })

  // [RGM] 2026-10-02 (E): campos de texto LIBRE descriptivos capitalizan la
  // primera letra de cada palabra (regla Rafael); los NOMBRES siguen con
  // mayúscula completa (toUpperCase) y email/cédulas/códigos no se tocan.
  const setCampoCap = (campo: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [campo]: capitalizarPalabras(e.target.value) })

  const onFoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result)
      setForm((f) => ({ ...f, fotoBase64: dataUrl.replace(/^data:image\/[^;]+;base64,/, ''), fotoContentType: file.type || '' }))
    }
    reader.readAsDataURL(file)
  }

  // GPS automático (captura lat,lng con 6 decimales; el formato lo valida
  // el BC cliente_bc.cs:516 — regex GX de la geolocalización); devuelve la
  // geo capturada ('' si no hay permiso/posición) para reintentar. Al
  // editar NO pisa la geolocalización ya guardada.
  const capturarPosicion = (): Promise<string> => {
    return new Promise((resolve) => {
      if (!('geolocation' in navigator)) return resolve('')
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const geo = `${pos.coords.latitude.toFixed(6)},${pos.coords.longitude.toFixed(6)}`
          setForm((f) => (f.geolocalizacion ? f : { ...f, geolocalizacion: geo }))
          resolve(geo)
        },
        () => resolve(''),
        { enableHighAccuracy: false, timeout: 10000 },
      )
    })
  }

  const abrirNuevo = () => {
    setEditando(null)
    setClonando(null)
    setRutasOcupadas(new Set())
    setForm(formVacio)
    setAbierto(true)
    capturarPosicion()
  }

  // Modificar (clienteww.cs:2902-2904): carga la fila en el form y abre el modal.
  // OJO: el grid expone ClienteObservacion (singular, lo setea el BC con
  // "Nuevo"/"Renovo" en INS — cliente_bc.cs:505-515), distinto de
  // ClienteObservaciones (plural, editable) → no se precarga (no pisar).
  const abrirEditar = (fila: any) => {
    setErrorModal('')
    setEditando(fila)
    setClonando(null)
    setRutasOcupadas(new Set())
    setForm({
      ...formVacio,
      fotoUrl: fila.fotoUrl ?? '',
      firmaClienteUrl: fila.firmaClienteUrl ?? '',
      firmaCodeudorUrl: fila.firmaCodeudorUrl ?? '',
      // El grid expone ClienteGeolocalizacion (char(50) con padding GX → trim).
      geolocalizacion: (fila.clienteGeolocalizacion ?? '').trim(),
      clienteCedula: String(fila.clienteCedula ?? ''),
      // [RGM] 2026-10-06 (reporte Rafael: cursor quedaba tras los espacios de
      // relleno GX): los campos char de la KB GX vienen con padding — se
      // recortan al cargar para que el cursor quede al final del texto real.
      nombre: String(fila.clienteNombre ?? '').trim(),
      negocio: String(fila.clienteNegocio ?? '').trim(),
      estado: fila.clienteEstado ?? 'Activo',
      celular: String(fila.clienteCelular ?? '').trim(),
      direccion: String(fila.clienteDireccion ?? '').trim(),
      barrioDireccion: String(fila.clienteBarrioDireccion ?? '').trim(),
      // [RGM] 2026-10-06 (reporte Rafael: "editar cliente queda en Seleccione
      // ruta"): pre-selecciona la RUTA ACTUAL del cliente (código de zona que
      // ahora expone el listado; el nombre solo no alcanza para el select).
      zonaCodigo: String(fila.zonaCodigo ?? ''),
    })
    setAbierto(true)
  }

  // [RGM] 2026-10-01 (extensión "clonado de cliente" — aprobada por Rafael):
  // "Clonar" abre el MISMO formulario de alta PRE-LLENADO con los datos del
  // cliente ORIGEN, EXCEPTO la cédula (queda vacía para teclear la nueva) y
  // la ruta (clonado-multiruta: se elige SIN prefill porque la del origen
  // está bloqueada — la misma persona no puede estar dos veces en una ruta).
  // El vínculo viaja oculto en form.clonadoDe = cédula origen. El detalle
  // completo se trae del endpoint GET /clientes/{cedula} (el grid solo
  // expone un subconjunto de campos).
  const abrirClonar = async (fila: any) => {
    setErrorModal('')
    setError('')
    setEditando(null)
    setClonando(fila)
    setRutasOcupadas(new Set())
    let detalle: any = {}
    try {
      detalle = await api.get<any>(`/clientes/${fila.clienteCedula}?empresa=${empresa}`)
    } catch {
      detalle = {}
    }
    // [RGM] 2026-10-01 (clonado-multiruta): rutas ocupadas = zonaCodigo de
    // TODA la familia del origen (consolidado). Si el consolidado falla, el
    // combo queda libre pero el backend igual rechaza la ruta ocupada.
    try {
      const cons = await api.get<any>(`/clientes/${fila.clienteCedula}/consolidado?empresa=${empresa}`)
      const ocupadas: number[] = (cons.familia ?? [])
        .map((m: any) => Number(m.zonaCodigo))
        .filter((n: number) => Number.isFinite(n) && n > 0)
      setRutasOcupadas(new Set(ocupadas))
    } catch {
      // sin consolidado → sin rutas bloqueadas en el combo (el backend valida)
    }
    setForm({
      ...formVacio,
      fotoUrl: detalle.fotoUrl ?? fila.fotoUrl ?? '',
      firmaClienteUrl: detalle.firmaClienteUrl ?? fila.firmaClienteUrl ?? '',
      firmaCodeudorUrl: detalle.firmaCodeudorUrl ?? fila.firmaCodeudorUrl ?? '',
      geolocalizacion: String(detalle.clienteGeolocalizacion ?? fila.clienteGeolocalizacion ?? '').trim(),
      // Cédula NUEVA vacía — se teclea la cédula del clon.
      clienteCedula: '',
      // Vínculo oculto con el ORIGINAL.
      clonadoDe: String(fila.clienteCedula ?? ''),
      nombre: String(detalle.clienteNombre ?? fila.clienteNombre ?? '').trim(),
      negocio: String(detalle.clienteNegocio ?? fila.clienteNegocio ?? '').trim(),
      estado: String(detalle.clienteEstado ?? fila.clienteEstado ?? 'Nuevo').trim(),
      celular: String(detalle.clienteCelular ?? fila.clienteCelular ?? '').trim(),
      telefono: String(detalle.clienteTelefono ?? '').trim(),
      email: detalle.clienteEmail ?? '',
      direccion: String(detalle.clienteDireccion ?? fila.clienteDireccion ?? '').trim(),
      barrioDireccion: String(detalle.clienteBarrioDireccion ?? fila.clienteBarrioDireccion ?? '').trim(),
      residencia: String(detalle.clienteReisdencia ?? '').trim(),
      barrioResidencia: String(detalle.clienteBarrioResidencia ?? '').trim(),
      referencia1: String(detalle.clienteReferencia1 ?? '').trim(),
      referencia2: String(detalle.clienteReferencia2 ?? '').trim(),
      codeudorCedula: detalle.clienteCodeudorCedula ? String(detalle.clienteCodeudorCedula) : '',
      codeudorNombre: String(detalle.clienteCodeudorNombre ?? fila.clienteCodeudorNombre ?? '').trim(),
      codeudorDireccion: String(detalle.clienteCodeudorDireccion ?? '').trim(),
      codeudorTelefono: String(detalle.clienteCodeudorTelefono ?? '').trim(),
      codeudorCelular: String(detalle.clienteCodeudorCelular ?? fila.clienteCodeudorCelular ?? '').trim(),
      observaciones: String(detalle.clienteObservaciones ?? '').trim(),
      // clonado-multiruta: la ruta destino se elige SIN prefill (la del
      // origen está ocupada por la familia).
      zonaCodigo: '',
      posicionRuta: detalle.clientePosicionRuta ? String(detalle.clientePosicionRuta) : '',
    })
    setAbierto(true)
  }

  const crear = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorModal('')
    setError('')
    setGuardando(true)
    try {
      // Geo: si la captura inicial falló, se reintenta AHORA al guardar; si
      // sigue sin posición, se avisa y el usuario decide (campo opcional).
      const geoFinal = form.geolocalizacion || (await capturarPosicion())
      if (!geoFinal) {
        const seguir = window.confirm(
          'No se pudo capturar la geolocalización del cliente.\n\n' +
            '¿Guardar el cliente sin geolocalización?',
        )
        if (!seguir) {
          setGuardando(false)
          return
        }
      }
      if (editando) {
        // clienteww.cs:2902-2904 — DO UPDATE: webobject cliente Mode="UPD" con
        // ClienteCedula (la PK viaja en la URL, no cambia). El backend replica
        // BC000910 (cliente_bc.cs:770) con validaciones CheckExtendedTable0928.
        await api.put(`/clientes/${editando.clienteCedula}`, {
          empresaCodigo: empresa,
          clienteNombre: form.nombre || undefined,
          clienteEstado: form.estado || undefined,
          clienteObservaciones: form.observaciones || undefined,
          clienteNegocio: form.negocio || undefined,
          clienteCelular: form.celular || undefined,
          clienteTelefono: form.telefono || undefined,
          clienteEmail: form.email || undefined,
          clienteDireccion: form.direccion || undefined,
          clienteBarrioDireccion: form.barrioDireccion || undefined,
          // ClienteReisdencia: typo GX preservado (type_SdtCliente.cs:243) — no renombrar
          clienteReisdencia: form.residencia || undefined,
          clienteBarrioResidencia: form.barrioResidencia || undefined,
          clienteGeolocalizacion: geoFinal || undefined,
          zonaCodigo: form.zonaCodigo ? Number(form.zonaCodigo) : undefined,
          posicionRuta: form.posicionRuta ? Number(form.posicionRuta) : undefined,
          clienteReferencia1: form.referencia1 || undefined,
          clienteReferencia2: form.referencia2 || undefined,
          codeudorCedula: form.codeudorCedula ? Number(form.codeudorCedula) : undefined,
          codeudorNombre: form.codeudorNombre || undefined,
          codeudorDireccion: form.codeudorDireccion || undefined,
          codeudorTelefono: form.codeudorTelefono || undefined,
          codeudorCelular: form.codeudorCelular || undefined,
          fotoBase64: form.fotoBase64 || undefined,
          fotoContentType: form.fotoContentType || undefined,
          firmaClienteBase64: form.firmaClienteBase64 || undefined,
          firmaCodeudorBase64: form.firmaCodeudorBase64 || undefined,
        })
      } else {
        await api.post('/clientes', {
          empresaCodigo: empresa,
          clienteCedula: Number(form.clienteCedula),
          nombre: form.nombre,
          zonaCodigo: Number(form.zonaCodigo) || 0,
          estado: form.estado || undefined,
          negocio: form.negocio || undefined,
          celular: form.celular || undefined,
          telefono: form.telefono || undefined,
          direccion: form.direccion || undefined,
          barrioDireccion: form.barrioDireccion || undefined,
          residencia: form.residencia || undefined,
          barrioResidencia: form.barrioResidencia || undefined,
          email: form.email || undefined,
          geolocalizacion: geoFinal || undefined,
          posicionRuta: Number(form.posicionRuta) || 0,
          referencia1: form.referencia1 || undefined,
          referencia2: form.referencia2 || undefined,
          observaciones: form.observaciones || undefined,
          codeudorCedula: form.codeudorCedula ? Number(form.codeudorCedula) : undefined,
          codeudorNombre: form.codeudorNombre || undefined,
          codeudorDireccion: form.codeudorDireccion || undefined,
          codeudorTelefono: form.codeudorTelefono || undefined,
          codeudorCelular: form.codeudorCelular || undefined,
          fotoBase64: form.fotoBase64 || undefined,
          fotoContentType: form.fotoContentType || undefined,
          firmaClienteBase64: form.firmaClienteBase64 || undefined,
          firmaCodeudorBase64: form.firmaCodeudorBase64 || undefined,
          // [RGM] 2026-10-01 (extensión "clonado de cliente"): 0 = alta
          // normal; > 0 = cédula del original cuando el alta es un clon.
          clienteClonadoDe: form.clonadoDe ? Number(form.clonadoDe) : 0,
        })
      }
      setAbierto(false)
      setEditando(null)
      setClonando(null)
      setRutasOcupadas(new Set())
      setForm(formVacio)
      cargar()
    } catch (err: any) {
      setErrorModal(err.message)
      setError(err.message)
    } finally {
      setGuardando(false)
    }
  }

  const eliminar = (fila: any) => {
    // clienteww.cs:2910-2912 — DO DELETE: webobject cliente Mode="DLT" con
    // ClienteCedula. El backend replica OnDeleteControls0928
    // (cliente_bc.cs:874-916): Factura → 409, Prestamo → 409, sin refs →
    // DELETE (BC000914).
    if (!window.confirm(`¿Eliminar el cliente ${fila.clienteCedula}?`)) return
    api.delete(`/clientes/${fila.clienteCedula}?empresa=${empresa}`)
      // DLT del cliente (clienteww.cs:2912 — Mode 'DLT' + GX_BtnDelete
      // clienteww.cs:2595)
      .then(() => {
        setAviso('Cliente eliminado.')
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  const contactar = (fila: any) => {
    const celular = String(fila.clienteCelular ?? '').trim()
    if (!celular) {
      setAviso('Cliente sin celular')
      return
    }
    window.open(`https://wa.me/${numeroWhatsApp(celular)}?text=${encodeURIComponent('Hola, te escribo sobre tu prestamo')}`, '_blank')
  }
  // El consolidado vive en el tab del cliente seleccionado (colapso + tabs);
  // el rowAction viejo 'Consolidado' se eliminó (duplicaba la lógica).
  // Acciones inline del grid — réplica del GX clienteww (Ver/Editar/Eliminar
  // + WhatsApp). Viven en el menú de acciones (no en columnas) y NO propagan
  // el click a la selección de fila (el menú del WwGrid los aísla).
  // Acciones de fila — TODAS dentro del menú ⋮ (decisión Rafael 2026-08-27:
  // estado original; una acción = una aparición — regla §11).
  const rowActions: WwRowAction[] = [
    // Consolidado/Contactar: ICONOS visibles en la columna de acciones
    // (caben 2 + ⋮ en 110px) — decisión Rafael 2026-08-27.
    { id: 'consolidado', label: 'Consolidado', icon: 'cilDescription', placement: 'inline-end', onClick: (fila) => setSeleccionado(fila) },
    { id: 'contactar', label: 'Contactar', icon: 'cilPhone', placement: 'inline-end', onClick: contactar },
    // [RGM] 2026-10-01 (extensión "clonado de cliente" — aprobada por
    // Rafael): misma persona en otra ruta con cédula nueva. Abre el alta
    // pre-llenada con el origen y la cédula vacía. Va al menú ⋮ (los 2
    // inline-end ya están ocupados por Consolidado/Contactar).
    { id: 'clonar', label: 'Clonar', icon: 'cilCopy', onClick: abrirClonar },
    { id: 'ver', label: 'Ver', icon: 'cilSearch', onClick: (fila) => navigate(`/clientes/${fila.clienteCedula}`) },
    { id: 'editar', label: 'Editar', icon: 'cilPencil', onClick: abrirEditar },
    { id: 'eliminar', label: 'Eliminar', icon: 'cilTrash', destructive: true, onClick: eliminar },
  ]

  const tarjeta = (icono: any, titulo: string, contenido: React.ReactNode) => (
    <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
      <CCardHeader className=" d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
        <CIcon icon={icono} size="sm" /> {titulo}
      </CCardHeader>
      <CCardBody style={{ padding: '14px 16px' }}>
        <CRow className="g-3">{contenido}</CRow>
      </CCardBody>
    </CCard>
  )

  const [totalReg, setTotalReg] = useState(rows.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Cliente</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      {/* ── Cliente seleccionado: colapso + tabs (patrón Empresas superadmin) ── */}
      {seleccionado && (
        <CCard className="shadow-sm mb-3">
          <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              {/* Foto del cliente (R2) en círculo — patrón del móvil
                  (resumen_cliente_screen.dart:513, fallback del movil) */}
              <div
                style={{
                  width: 88, height: 88, borderRadius: 44, overflow: 'hidden', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#078bcd', color: '#fff', fontSize: 40,
                }}
              >
                {seleccionado.fotoUrl ? (
                  <img src={seleccionado.fotoUrl} alt="Foto" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <CIcon icon={cilUser} size="xxl" />
                )}
              </div>
              <div>
                <div className="fs-5 fw-bold">
                {seleccionado.clienteCedula} · {seleccionado.clienteNombre?.trim()}
                </div>
              <div className="text-body-secondary small mt-1">
                {(() => {
                  const e = String(seleccionado.clienteEstado ?? '').trim()
                  const color = e === 'Activo' ? 'success' : e === 'Nuevo' ? 'info' : e === 'Inactivo' ? 'warning' : e === 'Eliminado' ? 'danger' : 'secondary'
                  return <CBadge color={color}>{e || '—'}</CBadge>
                })()}{' '}
                Ruta: {seleccionado.zonaNombre?.trim() || '—'} · Celular: {seleccionado.clienteCelular?.trim() || '—'}
              </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <CButton size="sm" color="primary" onClick={() => navigate(`/prestamos?cliente=${seleccionado.clienteCedula}`)}>
                + Nuevo Préstamo
              </CButton>
              <CButton size="sm" color="outline-secondary" onClick={() => setSeleccionado(null)}>
                ← Volver al listado
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      )}
      {seleccionado && (
        <CTabs defaultActiveItemKey="consolidado" className="mb-3">
          <CTabList variant="tabs">
            <CTab itemKey="consolidado">Consolidado</CTab>
            {/* Tab Facturas SOLO con el módulo de facturación activo
                (EmpresaModuloFacturacion — MenuController modfactura) */}
            {modFacturacion && <CTab itemKey="facturas">Facturas</CTab>}
          </CTabList>
          <CTabContent>
            <CTabPanel className="pt-3" itemKey="consolidado">
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                {/* [RGM] 2026-10-01 (extensión "clonado de cliente"): toggle
                    opt-in SOLO si hay cédulas vinculadas; default OFF para no
                    cambiar la vista actual. */}
                {(consolidado?.clonadoDe || (consolidado?.clones?.length ?? 0) > 0) ? (
                  <CFormCheck
                    checked={incluirClones}
                    onChange={(e) => setIncluirClones(e.target.checked)}
                    label={cargandoClones ? 'Incluir clones…' : 'Incluir clones'}
                  />
                ) : <span />}
                <CButton size="sm" color="outline-primary" onClick={() => descargarConsolidadoPdf(seleccionado)}>
                  Descargar PDF Consolidado
                </CButton>
              </div>
              {cargandoConsolidado ? (
                <div className="text-center py-4"><CSpinner color="primary" /></div>
              ) : !consolidado ? (
                <div className="text-body-secondary py-3 text-center">Sin datos</div>
              ) : (
                <>
                  {/* [RGM] 2026-10-01: avisos de vínculo (aditivos; toleran
                      respuestas sin los campos nuevos). */}
                  {consolidado.clonadoDe && (
                    <CAlert color="info" className="py-2 d-flex align-items-center gap-2 flex-wrap">
                      <span>
                        Mismo cliente — este es un clon de la cédula {consolidado.clonadoDe.cedula}{' '}
                        ({consolidado.clonadoDe.nombre}) en {consolidado.clonadoDe.rutaNombre || '—'}.
                      </span>
                      <CButton size="sm" color="info" variant="outline" onClick={() => verClienteVinculado(consolidado.clonadoDe.cedula)}>
                        Ver ese cliente
                      </CButton>
                    </CAlert>
                  )}
                  {((consolidado.clones ?? []).length > 0) && (
                    <CAlert color="info" className="py-2">
                      <div>
                        Este cliente tiene {consolidado.clones.length} clon(es) en otras rutas:
                      </div>
                      {consolidado.clones.map((cl: any) => (
                        <div key={cl.cedula} className="d-flex align-items-center gap-2">
                          <span>céd. {cl.cedula} — Ruta {cl.rutaNombre || '—'} ({cl.estado || '—'})</span>
                          <CButton size="sm" color="info" variant="outline" className="py-0" onClick={() => verClienteVinculado(cl.cedula)}>
                            Ver
                          </CButton>
                        </div>
                      ))}
                    </CAlert>
                  )}
                  <CRow className="g-3 mb-3">
                    {[
                      { label: 'Préstamos', valor: consolidado.kpis.totalPrestamos, color: 'text-primary' },
                      { label: 'Activos', valor: consolidado.kpis.prestamosActivos, color: 'text-success' },
                      { label: 'Saldo total', valor: fmtMoneda(consolidado.kpis.saldoTotal), color: 'text-warning' },
                      { label: 'Abonos acumulados', valor: fmtMoneda(consolidado.kpis.abonosAcumulados), color: 'text-info' },
                    ].map((k) => (
                      <CCol key={k.label} md={3} sm={6}>
                        <CCard className="text-center h-100 shadow-sm">
                          <CCardBody>
                            <div className={`fs-3 fw-bold ${k.color}`}>{k.valor ?? '—'}</div>
                            <div className="text-body-secondary small">{k.label}</div>
                          </CCardBody>
                        </CCard>
                      </CCol>
                    ))}
                  </CRow>
                  <ConsolidadoPorEstado prestamos={prestamosConsolidado} mostrarCedula={incluirClones} />
                </>
              )}
            </CTabPanel>

            {modFacturacion && (
            <CTabPanel className="pt-3" itemKey="facturas">
              <WwGrid
                title="Facturas"
                columns={[
                  { key: 'facturaNro', title: 'Nro', type: 'number' },
                  { key: 'facturaFecha', title: 'Fecha', type: 'date' },
                  { key: 'facturaHora', title: 'Hora' },
                  { key: 'facturaCreadaPor', title: 'Creada Por' },
                ]}
                rows={rows.filter((r) => String(r.clienteCedula ?? '') === String(seleccionado.clienteCedula ?? ''))}
                onTotalChange={() => undefined}
              />
            </CTabPanel>
            )}
          </CTabContent>
        </CTabs>
      )}

      {!seleccionado && (
        <WwGrid
          filters={
            <WwFilterBar
              selects={[
                { key: 'estado', label: 'Todos los Estados', opciones: estadosCliente, valor: filtroEstado, onChange: setFiltroEstado },
                { key: 'ruta', label: 'Todas las Rutas', opciones: rutasCliente, valor: filtroRuta, onChange: setFiltroRuta },
              ]}
              chips={chips}
              onQuitar={quitarFiltro}
              onLimpiar={limpiarFiltros}
            />
          }
          title="Cliente"
          columns={columnas}
          rows={visibleRows}
          onTotalChange={setTotalReg}
          loading={cargando}
          onRowClick={(row) => setSeleccionado(row)}
          onAgregar={abrirNuevo}
          // Las acciones inline (Ver/Editar/Eliminar/Contactar) viven en
          // rowActions (réplica GX clienteww) — sin onEditar/onEliminar
          // duplicados del grid.
          busquedaPlaceholder="Buscar por cédula, nombre o celular..." 
          rowActions={rowActions}
          onRowLink={(colKey, row) => {
            if (colKey === 'clienteCedula') navigate(`/clientes/${row.clienteCedula}`)
          }}
        />
      )}

      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null); setClonando(null); setRutasOcupadas(new Set()) }} size="lg" className="ww-modal-cliente">
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar cliente' : clonando ? 'Clonar cliente' : 'Nuevo cliente'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          {/* [RGM] 2026-10-01 (extensión "clonado de cliente"): aviso visible
              del vínculo con el cliente original. */}
          {clonando && (
            <CAlert color="info" className="py-2">
              Clon del cliente cédula {clonando.clienteCedula} ({String(clonando.clienteNombre ?? '').trim()}) —
              mismo cliente en otra ruta. Asigne la nueva cédula y la ruta destino.
            </CAlert>
          )}
          <CForm ref={formRef} onSubmit={crear}>
            {tarjeta(cilUser, 'Información General', (
              <>
                <CCol md={6}>
                  <CFormLabel>Nombre Completo</CFormLabel>
                  {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
                  <CFormInput value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Cédula / NUIP / ID</CFormLabel>
                  <CFormInput type="number" value={form.clienteCedula} disabled={editando !== null} onChange={setCampo('clienteCedula')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Negocio</CFormLabel>
                  <CFormInput value={form.negocio} onChange={setCampoCap('negocio')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Estado</CFormLabel>
                  <CFormSelect value={form.estado} onChange={setCampo('estado')}>
                    {estados.map((e) => <option key={e} value={e}>{e}</option>)}
                  </CFormSelect>
                </CCol>
                {/* Campo Foto SOLO con el módulo de fotos activo
                    (EmpresaFactura — caption GX "Fotos Adicional Mes") */}
                {modFotos && (
                  <CCol md={12}>
                    <CFormLabel>Foto</CFormLabel>
                    <CFormInput type="file" accept="image/*" onChange={onFoto} />
                    {(form.fotoBase64 || form.fotoUrl) && (
                      <img
                        src={form.fotoBase64 || form.fotoUrl}
                        alt="Foto cliente"
                        style={{ maxWidth: 120, maxHeight: 90, marginTop: 8, borderRadius: 6 }}
                       />
                     )}
                  </CCol>
                )}
              </>
            ))}

            {/* [RGM] 2026-10-02 (reporte Rafael): tarjeta "Ruta" en 2ª
                posición del form (antes iba después de Direcciones). */}
            {tarjeta(cilMap, 'Ruta', (
              <>
                <CCol md={6}>
                  <CFormLabel>Ruta</CFormLabel>
                  <CFormSelect value={form.zonaCodigo} onChange={setCampo('zonaCodigo')}>
                    <option value="">Seleccione ruta</option>
                    {rutas.map((r) => {
                      // [RGM] 2026-10-01 (clonado-multiruta): al clonar, las
                      // rutas ya ocupadas por la familia quedan disabled
                      // ("ya está en esta ruta"); el resto normales.
                      const ocupada = rutasOcupadas.has(Number(r.zonaCodigo))
                      return (
                        <option key={r.zonaCodigo} value={r.zonaCodigo} disabled={ocupada}>
                          {r.zonaNombre}{ocupada ? ' — ya está en esta ruta' : ''}
                        </option>
                      )
                    })}
                  </CFormSelect>
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Posición Ruta</CFormLabel>
                  <CFormInput type="number" value={form.posicionRuta} onChange={setCampo('posicionRuta')} />
                </CCol>
              </>
            ))}

            {tarjeta(cilPhone, 'Contacto', (
              <>
                <CCol md={12}>
                  <CFormLabel>Celular / WhatsApp</CFormLabel>
                  <CInputGroup>
                    <CInputGroupText>{prefijoPais()}</CInputGroupText>
                    <CFormInput value={form.celular} onChange={setCampo('celular')} />
                  </CInputGroup>
                </CCol>
                <CCol md={12}>
                  <CFormLabel>Email</CFormLabel>
                  <CFormInput type="email" value={form.email} onChange={setCampo('email')} />
                </CCol>
              </>
            ))}

            {tarjeta(cilLocationPin, 'Direcciones', (
              <>
                <CCol md={6}>
                  <CFormLabel>Dirección Negocio</CFormLabel>
                  <CFormInput value={form.direccion} onChange={setCampoCap('direccion')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Barrio</CFormLabel>
                  <CFormInput value={form.barrioDireccion} onChange={setCampoCap('barrioDireccion')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Dirección Residencia</CFormLabel>
                  <CFormInput value={form.residencia} onChange={setCampoCap('residencia')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Barrio Residencia</CFormLabel>
                  <CFormInput value={form.barrioResidencia} onChange={setCampoCap('barrioResidencia')} />
                </CCol>
                <CCol md={12}>
                  <CFormLabel>Geolocalización</CFormLabel>
                  <div className="d-flex gap-2">
                    <CFormInput placeholder="lat,lng" value={form.geolocalizacion} onChange={setCampo('geolocalizacion')} />
                    <CButton type="button" size="sm" color="info" variant="outline" onClick={capturarPosicion} title="Capturar posición GPS (réplica gxMap GX)">
                      <i className="fas fa-location-dot" />
                    </CButton>
                  </div>
                </CCol>
              </>
            ))}

            {tarjeta(cilAddressBook, 'Referencias', (
              <>
                <CCol md={6}>
                  <CFormLabel>Referencia 1</CFormLabel>
                  <CFormInput value={form.referencia1} onChange={setCampoCap('referencia1')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Referencia 2</CFormLabel>
                  <CFormInput value={form.referencia2} onChange={setCampoCap('referencia2')} />
                </CCol>
              </>
            ))}

            {tarjeta(cilUserFollow, 'Codeudor', (
              <>
                <CCol md={6}>
                  <CFormLabel>Nombre</CFormLabel>
                  {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
                  <CFormInput value={form.codeudorNombre} onChange={(e) => setForm({ ...form, codeudorNombre: e.target.value.toUpperCase() })} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Cédula</CFormLabel>
                  <CFormInput type="number" value={form.codeudorCedula} onChange={setCampo('codeudorCedula')} />
                </CCol>
                <CCol md={12}>
                  <CFormLabel>Celular</CFormLabel>
                  <CInputGroup>
                    <CInputGroupText>{prefijoPais()}</CInputGroupText>
                    <CFormInput value={form.codeudorCelular} onChange={setCampo('codeudorCelular')} />
                  </CInputGroup>
                </CCol>
                <CCol md={12}>
                  <CFormLabel>Dirección</CFormLabel>
                  <CFormInput value={form.codeudorDireccion} onChange={setCampoCap('codeudorDireccion')} />
                </CCol>
              </>
            ))}

            {tarjeta(cilPencil, 'Observaciones y Firmas', (
              <>
                <CCol md={12}>
                  <CFormLabel>Observaciones</CFormLabel>
                  <CFormTextarea rows={3} value={form.observaciones} onChange={setCampoCap('observaciones')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Firma Cliente</CFormLabel>
                  {/* La firma existente (R2) se muestra mientras no se firme una nueva */}
                  {form.firmaClienteUrl && !form.firmaClienteBase64 && (
                    <img src={form.firmaClienteUrl} alt="Firma cliente" style={{ maxWidth: 200, maxHeight: 80, border: '1px solid #ccc', borderRadius: 4 }} />
                  )}
                  <FirmaCanvas
                    value={form.firmaClienteBase64}
                    onChange={(b64) => setForm({ ...form, firmaClienteBase64: b64 ?? '' })}
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Firma Codeudor</CFormLabel>
                  {form.firmaCodeudorUrl && !form.firmaCodeudorBase64 && (
                    <img src={form.firmaCodeudorUrl} alt="Firma codeudor" style={{ maxWidth: 200, maxHeight: 80, border: '1px solid #ccc', borderRadius: 4 }} />
                  )}
                  <FirmaCanvas
                    value={form.firmaCodeudorBase64}
                    onChange={(b64) => setForm({ ...form, firmaCodeudorBase64: b64 ?? '' })}
                  />
                </CCol>
              </>
            ))}
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => { setAbierto(false); setEditando(null); setClonando(null); setRutasOcupadas(new Set()) }}>Cancelar</CButton>
          <CButton color="primary" type="button" disabled={guardando} onClick={() => formRef.current?.requestSubmit()}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </CButton>
        </CModalFooter>
      </CModal>

    </>
  )
}

export default ClientesPage
